import React from "react";
import { Button, Dropdown, Tooltip } from "antd";
import { MoreOutlined } from "@ant-design/icons";

/**
 * Overflow ("three dot") menu for table row actions.
 *
 * Collapses the per-row icon buttons into a single kebab trigger so the
 * Actions column stays one control wide no matter how many actions a row has.
 *
 * `items` accepts falsy entries so callers can inline permission checks:
 *   items={[canViewHistory && { key: "history", ... }, { key: "edit", ... }]}
 *
 * An action may carry a `tooltip`, which is the right place for the reason an
 * action is unavailable. Putting that reason in the `label` instead stretches
 * the dropdown to the width of the sentence, which is how this menu ended up
 * several hundred pixels wide for one disabled entry.
 */
const RowActions = ({ items, disabled = false, placement = "bottomRight" }) => {
  const actions = items.filter(Boolean);

  if (actions.length === 0) return null;

  const menuItems = actions.map((action) => ({
    key: action.key,
    label: action.tooltip ? (
      // antd sets `pointer-events: none` on a disabled menu item, which would
      // swallow the hover the tooltip needs; re-enabling it on the label
      // itself is what lets a disabled action still explain itself.
      <Tooltip title={action.tooltip} placement="left">
        <span style={{ pointerEvents: "auto" }}>{action.label}</span>
      </Tooltip>
    ) : (
      action.label
    ),
    icon: action.icon,
    danger: action.danger,
    disabled: action.disabled,
  }));

  const handleClick = ({ key, domEvent }) => {
    // Keep the click from reaching row-level handlers (e.g. clickable rows).
    domEvent?.stopPropagation();
    actions.find((action) => action.key === key)?.onClick?.();
  };

  return (
    <Dropdown
      menu={{ items: menuItems, onClick: handleClick }}
      trigger={["click"]}
      placement={placement}
      disabled={disabled}
    >
      <Button
        type="text"
        size="small"
        icon={<MoreOutlined />}
        aria-label="Row actions"
        onClick={(event) => event.stopPropagation()}
      />
    </Dropdown>
  );
};

export default RowActions;
